import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { ModalProps } from '@swyftx/aviary/atoms/Modal/Modal.styles';
import { Separator } from '@swyftx/aviary/atoms/Seperator/Seperator';
import { Body, Heading, Numeric, Utility } from '@swyftx/aviary/atoms/Typography';
import { InformationSimple } from '@swyftx/aviary/icons/outlined';
import { Menu, MenuProps } from '@swyftx/aviary/molecules/Menu';
import { ReviewItem } from '@swyftx/aviary/molecules/ReviewItem';

import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { useMarginAccount } from 'src/lib/margin/hooks/account';
import { useMarginInstruments } from 'src/lib/margin/hooks/instruments';
import { useMarginPrices } from 'src/lib/margin/hooks/prices';
import { getMarketPrecisionScale } from 'src/lib/margin/utils';
import { useMarkets } from 'src/lib/markets/hooks/useMarkets';

import { MarginInstrumentInformationTradingHours } from './MarginInstrumentInformationTradingHours';

type Props = {
  variant?: MenuProps['variant'];
  animate?: ModalProps['animate'];
};

const MarginInstrumentInformation: React.FC<Props> = ({ variant = 'standard', animate }) => {
  const [open, setOpen] = useState<boolean>(false);
  const { getAssetsByUuid } = useMarkets();
  const { selectedMarketPrice } = useMarginPrices();
  const { selectedMarket, secondaryCode } = useMarginInstruments();
  const { commission } = useMarginAccount();

  const { t } = useTranslation('margin');

  const assets = useMemo(() => {
    if (!selectedMarket) return [];

    return getAssetsByUuid([selectedMarket.secondaryAssetUuid, selectedMarket.primaryAssetUuid]);
  }, [getAssetsByUuid, selectedMarket]);

  return (
    <Menu
      variant={variant}
      open={open}
      position='dynamic'
      title={t('instruments.information.title')}
      onOpenChange={setOpen}
      sideOffset={0}
      padding={false}
      className='w-[400px]'
      align='start'
      animate={animate}
      triggerContent={
        <Button
          variant='outlined'
          size='md'
          layout='icon'
          leadingIcon={<InformationSimple />}
          disabled={!selectedMarket}
          tooltip={t('instruments.information.tooltip')}
        />
      }
    >
      <FlexLayout direction='column' spacing={12} className='p-12 md:max-h-[75vh] md:overflow-auto'>
        <Heading size='h4' className='hidden md:block'>
          {t('instruments.information.title')}
        </Heading>
        <FlexLayout direction='column' spacing={12}>
          <ReviewItem title={t('instruments.information.instrument.title')}>
            <Body weight='bold'>{assets.map((a) => a.code).join('')}</Body>
          </ReviewItem>
          <ReviewItem title={t('instruments.information.name.title')}>
            <Body weight='bold'>{assets.map((a) => a.name).join(' / ')}</Body>
          </ReviewItem>
          <Separator />
          <Utility variant='caption' color='primary' weight='bold'>
            {t('instruments.information.tradingDetails')}
          </Utility>
          <ReviewItem
            title={t('instruments.information.leverage.title')}
            underline='dashed'
            tooltip={t('instruments.information.leverage.tooltip')}
          >
            <Numeric weight='emphasis'>1:{selectedMarket?.maxLeverage}</Numeric>
          </ReviewItem>
          <ReviewItem
            title={t('instruments.information.spread.title')}
            underline='dashed'
            tooltip={t('instruments.information.spread.tooltip')}
          >
            <Numeric weight='emphasis'>{Big(selectedMarketPrice?.spreadPips || 0).toFixed(2)}</Numeric>
          </ReviewItem>
          <ReviewItem
            title={t('instruments.information.lotSize.title')}
            underline='dashed'
            tooltip={t('instruments.information.lotSize.tooltip')}
          >
            <Numeric weight='emphasis'>{selectedMarket?.contractSize}</Numeric>
          </ReviewItem>
          <ReviewItem
            title={t('instruments.information.minLotSize.title', { code: secondaryCode })}
            underline='dashed'
            tooltip={t('instruments.information.minLotSize.tooltip')}
          >
            <Numeric weight='emphasis'>{formatCurrency(selectedMarket?.minLots)}</Numeric>
          </ReviewItem>
          <ReviewItem
            title={t('instruments.information.maxLotSize.title', { code: secondaryCode })}
            underline='dashed'
            tooltip={t('instruments.information.maxLotSize.tooltip')}
          >
            <Numeric weight='emphasis'>
              {formatCurrency(selectedMarket?.maxLots, undefined, {
                priceScale: getMarketPrecisionScale(selectedMarket?.minLotIncrement),
              })}
            </Numeric>
          </ReviewItem>
          <ReviewItem
            title={t('instruments.information.sizeIncrement.title')}
            underline='dashed'
            tooltip={t('instruments.information.sizeIncrement.tooltip')}
          >
            <Numeric weight='emphasis'>{selectedMarket?.minLotIncrement}</Numeric>
          </ReviewItem>
          <ReviewItem
            title={t('instruments.information.pricePrecision.title')}
            underline='dashed'
            tooltip={t('instruments.information.pricePrecision.tooltip')}
          >
            <Numeric weight='emphasis'>
              {Big(Math.pow(10, -(selectedMarket?.precision || 0))).toFixed(selectedMarket?.precision)}
            </Numeric>
          </ReviewItem>
          <Separator />
          <MarginInstrumentInformationTradingHours market={selectedMarket} />
          <Separator />
          <Utility variant='caption' color='primary' weight='bold'>
            {t('instruments.information.fees')}
          </Utility>
          <ReviewItem
            title={t('instruments.information.commission.title')}
            underline='dashed'
            tooltip={t('instruments.information.commission.tooltip')}
          >
            <FlexLayout direction='column' alignItems='end'>
              <Numeric weight='emphasis'>{Big(commission).times(100).toFixed(2)}%</Numeric>
              <Body size='xsmall' color='secondary'>
                {t('instruments.information.commission.content')}
              </Body>
            </FlexLayout>
          </ReviewItem>
          <ReviewItem
            title={t('instruments.information.overnightFeeLong.title')}
            underline='dashed'
            tooltip={t('instruments.information.overnightFeeLong.tooltip')}
          >
            <FlexLayout direction='column' alignItems='end'>
              <Numeric weight='emphasis'>
                {formatCurrency(Big(selectedMarketPrice?.swapLong).abs(), undefined, { priceScale: 2 })}% p.a.
              </Numeric>
              <Body size='xsmall' color='secondary'>
                {t('instruments.information.overnightFeeLong.charged')}
              </Body>
            </FlexLayout>
          </ReviewItem>
          <ReviewItem
            title={t('instruments.information.overnightFeeShort.title')}
            underline='dashed'
            tooltip={t('instruments.information.overnightFeeShort.tooltip')}
          >
            <FlexLayout direction='column' alignItems='end'>
              <Numeric weight='emphasis'>
                {formatCurrency(Big(selectedMarketPrice?.swapShort).abs(), undefined, { priceScale: 2 })}% p.a.
              </Numeric>
              <Body size='xsmall' color='secondary'>
                {t('instruments.information.overnightFeeShort.credited')}
              </Body>
            </FlexLayout>
          </ReviewItem>
          <ReviewItem
            title={t('instruments.information.longHoldFee.title')}
            name={t('instruments.information.longHoldFee.name')}
            underline='dashed'
            tooltip={t('instruments.information.longHoldFee.tooltip')}
          >
            <FlexLayout direction='column' alignItems='end'>
              <Numeric weight='emphasis'>{Number(20).toFixed(2)}% p.a.</Numeric>
              <Body size='xsmall' color='secondary'>
                {t('instruments.information.longHoldFee.chargedWeekly')}
              </Body>
            </FlexLayout>
          </ReviewItem>
          <Utility variant='caption' color='secondary'>
            {t('instruments.information.overnightFees', { time: '07:00 AM' })}
          </Utility>
          <Utility variant='caption' color='secondary'>
            {t('instruments.information.longFees', { percentage: '20.00' })}
          </Utility>
        </FlexLayout>
      </FlexLayout>
    </Menu>
  );
};

export { MarginInstrumentInformation };
